import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { AddressInput, cleanupPacContainers } from "@/components/form-fields/address-input";
import { formatABN } from "@/utils/formatters";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { insertPartnerSchema, type Partner } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";

// Explicit form type definition to avoid Zod type inference issues
type PartnerFormData = {
  partnerName: string;
  partnerType?: string | null;
  partnerTier?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  website?: string | null;
  streetAddress?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  abn?: string | null;
  companyName?: string | null;
  upfrontCommissionRate?: string | null;
  trailCommissionRate?: string | null;
  paymentTermsDays?: number | null;
  commissionNotes?: string | null;
  rctiRecipientName?: string | null;
  rctiEmail?: string | null;
  gstRegistered?: boolean;
  defaultCommissionRate?: string | null;
  commissionPaymentTerms?: string | null;
  isActive?: boolean;
  xeroContactId?: string | null;
  contactId?: string | null;
  accountId?: string | null;
  status?: string | null;
  partnerSince?: string | null;
  notes?: string | null;
  organisationId?: string | null;
};

interface PartnerFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partner?: Partner;
  onPartnerCreated?: (partner: Partner) => void;
}

const PARTNER_TYPES = [
  { value: "mortgage_broker", label: "Mortgage Broker" },
  { value: "accountant", label: "Accountant" },
  { value: "financial_planner", label: "Financial Planner" },
  { value: "lawyer", label: "Lawyer" },
  { value: "real_estate_agent", label: "Real Estate Agent" },
  { value: "business_broker", label: "Business Broker" },
  { value: "other", label: "Other" },
];

const PARTNER_TIERS = [
  { value: "platinum", label: "Platinum" },
  { value: "gold", label: "Gold" },
  { value: "silver", label: "Silver" },
  { value: "bronze", label: "Bronze" },
];

const PARTNER_STATUS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "suspended", label: "Suspended" },
];

export function PartnerFormModal({ open, onOpenChange, partner, onPartnerCreated }: PartnerFormModalProps) {
  const { toast } = useToast();
  const isEdit = !!partner;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [useManualAddressEntry, setUseManualAddressEntry] = useState(false);
  const [hasGoogleMapsApi, setHasGoogleMapsApi] = useState(false);
  const [googleApiError, setGoogleApiError] = useState(false);

  const defaultValues = {
    partnerName: "",
    partnerType: null as string | null,
    partnerTier: null as string | null,
    email: null as string | null,
    phone: null as string | null,
    mobile: null as string | null,
    website: null as string | null,
    streetAddress: null as string | null,
    suburb: null as string | null,
    state: null as string | null,
    postcode: null as string | null,
    abn: null as string | null,
    companyName: null as string | null,
    // New commission fields (split upfront/trail)
    upfrontCommissionRate: "20.00" as string | null,
    trailCommissionRate: "20.00" as string | null,
    paymentTermsDays: 7 as number | null,
    commissionNotes: null as string | null,
    // RCTI fields
    rctiRecipientName: null as string | null,
    rctiEmail: null as string | null,
    gstRegistered: true,
    // Status and metadata
    status: "active" as string | null,
    partnerSince: null as string | null,
    // Legacy fields (kept for backward compatibility)
    defaultCommissionRate: null as string | null,
    commissionPaymentTerms: null as string | null,
    isActive: true,
    notes: null as string | null,
    xeroContactId: null as string | null,
    contactId: null as string | null,
    accountId: null as string | null,
    organisationId: null as string | null,
  };

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(insertPartnerSchema),
    defaultValues,
  });

  const normalizeForForm = (record: Partner): PartnerFormData => ({
    partnerName: record.partnerName,
    partnerType: record.partnerType ?? null,
    partnerTier: record.partnerTier ?? null,
    email: record.email ?? null,
    phone: record.phone ?? null,
    mobile: record.mobile ?? null,
    website: record.website ?? null,
    streetAddress: record.streetAddress ?? null,
    suburb: record.suburb ?? null,
    state: record.state ?? null,
    postcode: record.postcode ?? null,
    abn: record.abn ?? null,
    companyName: record.companyName ?? null,
    // New commission fields
    upfrontCommissionRate: record.upfrontCommissionRate?.toString() ?? "20.00",
    trailCommissionRate: record.trailCommissionRate?.toString() ?? "20.00",
    paymentTermsDays: record.paymentTermsDays ?? 7,
    commissionNotes: record.commissionNotes ?? null,
    // RCTI fields
    rctiRecipientName: record.rctiRecipientName ?? null,
    rctiEmail: record.rctiEmail ?? null,
    gstRegistered: record.gstRegistered ?? true,
    // Status and metadata
    status: record.status ?? "active",
    partnerSince: record.partnerSince ?? null,
    // Legacy fields
    defaultCommissionRate: record.defaultCommissionRate?.toString() ?? null,
    commissionPaymentTerms: record.commissionPaymentTerms ?? null,
    isActive: record.isActive ?? true,
    notes: record.notes ?? null,
    xeroContactId: record.xeroContactId ?? null,
    contactId: record.contactId ?? null,
    accountId: record.accountId ?? null,
    organisationId: record.organisationId ?? null,
  });

  useEffect(() => {
    if (open) {
      if (partner) {
        form.reset(normalizeForForm(partner));
      } else {
        form.reset(defaultValues);
      }
    }
  }, [open, partner, form]);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    setHasGoogleMapsApi(!!apiKey);
    setUseManualAddressEntry(!apiKey);
  }, []);

  const handleGoogleApiError = () => {
    setGoogleApiError(true);
    setUseManualAddressEntry(true);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
      setGoogleApiError(false);
      setUseManualAddressEntry(!hasGoogleMapsApi);
      // Clean up Google Places autocomplete dropdown
      cleanupPacContainers();
    }
    onOpenChange(newOpen);
  };

  const createMutation = useMutation({
    mutationFn: async (data: PartnerFormData): Promise<Partner> => {
      const response = await apiRequest("POST", "/api/crm/partners", data);
      return response.json();
    },
    onSuccess: (newPartner: Partner) => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/partners"] });
      toast({
        title: "Success",
        description: "Partner created successfully",
      });

      // Call the callback if provided (for auto-linking from contact/account forms)
      if (onPartnerCreated) {
        onPartnerCreated(newPartner);
      } else {
        handleOpenChange(false);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create partner",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: PartnerFormData) =>
      apiRequest("PATCH", `/api/crm/partners/${partner?.id}`, data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/partners"] });
      toast({
        title: "Success",
        description: "Partner updated successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update partner",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest("DELETE", `/api/crm/partners/${partner?.id}`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["/api/crm/partners"] });
      toast({
        title: "Success",
        description: "Partner deleted successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete partner",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: PartnerFormData) => {
    const payload = {
      ...data,
      defaultCommissionRate: data.defaultCommissionRate ? data.defaultCommissionRate : null,
    };

    if (isEdit) {
      updateMutation.mutate(payload as any);
    } else {
      createMutation.mutate(payload as any);
    }
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => {
          // Prevent dialog from closing when clicking Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Partner" : "Add Partner"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the partner information below"
              : "Enter the details for the new partner"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="partnerName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Partner Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., John Smith"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partnerType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Partner Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PARTNER_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Company or business name"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="abn"
                render={({ field }) => {
                  const abnValue = field.value || "";
                  const abnDigits = abnValue.replace(/\s/g, "");
                  const abnLength = abnDigits.length;
                  const abnIsValid = abnDigits === "" || (abnLength === 11 && /^\d+$/.test(abnDigits));
                  // Format ABN for display (XX XXX XXX XXX)
                  const formattedABN = formatABN(abnValue);

                  return (
                    <FormItem>
                      <FormLabel>ABN</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="12 345 678 901"
                          data-testid="input-partner-abn"
                          value={formattedABN}
                          onChange={(e) => {
                            // Strip spaces and store raw digits
                            const rawValue = e.target.value;
                            const strippedValue = rawValue.replace(/\s/g, "");
                            field.onChange(strippedValue);
                          }}
                          onPaste={(e) => {
                            // Handle paste event to strip spaces from pasted content
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData("text");
                            const strippedValue = pastedText.replace(/\s/g, "");
                            field.onChange(strippedValue);
                          }}
                        />
                      </FormControl>
                      {abnValue && !abnIsValid && (
                        <p className="text-sm text-destructive">
                          ABN must be exactly 11 digits (currently {abnLength} digits)
                        </p>
                      )}
                      <FormDescription className="text-xs">
                        Enter 11 digits. Displayed as XX XXX XXX XXX.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Business phone"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="mobile"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mobile</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Mobile phone"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input
                        type="url"
                        placeholder="https://www.example.com.au"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Partner's website URL
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="partnerTier"
                render={({ field}) => (
                  <FormItem>
                    <FormLabel>Partner Tier</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PARTNER_TIERS.map((tier) => (
                          <SelectItem key={tier.value} value={tier.value}>
                            {tier.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Commission Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Commission Rates</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="upfrontCommissionRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Upfront Commission (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="20.00"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormDescription>Partner's share of upfront</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="trailCommissionRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Trail Commission (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="20.00"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormDescription>Partner's share of trail</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="paymentTermsDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Terms (Days)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="7"
                          {...field}
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                        />
                      </FormControl>
                      <FormDescription>Days until payment due</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="commissionNotes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Commission Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Special commission arrangements..."
                        className="min-h-[60px]"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Address Section - Google Places Integration */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Address</h3>
              {hasGoogleMapsApi && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="manualAddressEntryPartner"
                    checked={useManualAddressEntry}
                    onChange={(e) => {
                      setUseManualAddressEntry(e.target.checked);
                      if (!e.target.checked) {
                        setGoogleApiError(false);
                      }
                    }}
                    className="rounded border-gray-300"
                    data-testid="checkbox-manual-address-entry-partner"
                  />
                  <FormLabel htmlFor="manualAddressEntryPartner" className="cursor-pointer text-sm font-normal">
                    Enter address manually
                  </FormLabel>
                </div>
              )}

              {useManualAddressEntry ? (
                <div className="space-y-4">
                  {googleApiError && (
                    <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      Google Maps unavailable. Please enter address manually.
                    </div>
                  )}
                  <FormField
                    control={form.control}
                    name="streetAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Street Address</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Street address"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-4 md:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="suburb"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Suburb</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Suburb"
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="State"
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="postcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postcode</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Postcode"
                              {...field}
                              value={field.value ?? ""}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <FormItem>
                    <FormLabel>Address</FormLabel>
                    <FormControl>
                      <AddressInput
                        value={form.watch("streetAddress") ?? ""}
                        onAddressSelect={(addressData) => {
                          form.setValue("streetAddress", addressData.formattedAddress ?? "");
                          form.setValue("suburb", addressData.suburb ?? "");
                          form.setValue("state", addressData.state ?? "");
                          form.setValue("postcode", addressData.postcode ?? "");
                        }}
                        onApiError={handleGoogleApiError}
                        data-testid="partner-address-input"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Start typing to search for Australian addresses
                    </FormDescription>
                  </FormItem>

                  {form.watch("streetAddress") && (
                    <div className="grid gap-4 pt-2 md:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="suburb"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Suburb</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Suburb"
                                {...field}
                                value={field.value ?? ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>State</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="State"
                                {...field}
                                value={field.value ?? ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="postcode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Postcode</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Postcode"
                                {...field}
                                value={field.value ?? ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* RCTI Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">RCTI Settings</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="rctiRecipientName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>RCTI Recipient Name</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="If different from partner name"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rctiEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>RCTI Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Where to send RCTIs"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="gstRegistered"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">GST Registered</FormLabel>
                      <FormDescription>
                        Is this partner registered for GST?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={field.onChange}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Status and Metadata Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Status & Metadata</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PARTNER_STATUS.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerSince"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Partner Since</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Additional notes about this partner"
                      className="min-h-[80px]"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <div className="flex w-full items-center justify-between gap-2">
                <div className="flex gap-2">
                  {isEdit && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setShowDeleteDialog(true)}
                      disabled={isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isEdit ? "Update Partner" : "Create Partner"}
                  </Button>
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the partner.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
